<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { DELIVERY_MODULE } from "@/state/modules/delivery";
import { SALES_DOC_MODULE } from "@/state/modules/salesDoc";
import { deliveryMethods, salesDocMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { getSalesDoc as fetchSourceDoc } from "@/helpers/services/salesDoc.service";

const STATUSES = ["pending", "dispatched", "delivered", "returned", "cancelled"];

export default {
  page: { title: i18n.t("module.delivery") },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      statuses: STATUSES,
      selectedStatus: "pending",
      form: { status: "pending", recipient_name: "", notes: "" },
      saving: false,

      // Return processing
      returnLines: [],
      returnNotes: "",
      submittingReturn: false,

      // Returned items shown on a return's detail page (pulled from the DR doc).
      returnedItems: [],
    };
  },
  computed: {
    ...mapState(DELIVERY_MODULE, ["details"]),
    ...mapState(SALES_DOC_MODULE, { sourceDoc: "details" }),
    d() { return this.details || {}; },
    isReturn() { return this.d.type === "return"; },
    isOutbound() { return this.d.type === "outbound"; },
    canProcessReturn() { return this.isOutbound && !!this.d.autocountDocNo; },
    title() {
      const prefix = this.isReturn ? "Return" : "Delivery";
      return this.d.autocountDocNo ? `${prefix} — ${this.d.autocountDocNo}` : i18n.t("module.delivery");
    },
    items() {
      return [
        { text: i18n.t("module.sales"), href: "/" },
        { text: i18n.t("module.delivery"), href: "/deliveries" },
        { text: this.d.autocountDocNo || i18n.t("label.view"), active: true },
      ];
    },
    selectedReturnLines() {
      return this.returnLines.filter((l) => l.selected && Number(l.returnQty) > 0);
    },
    hasInvalidReturnQty() {
      return this.returnLines.some((l) => l.selected && Number(l.returnQty) > Number(l.soldQty));
    },
    canSubmitReturn() {
      return this.selectedReturnLines.length > 0 && !this.hasInvalidReturnQty;
    },
  },
  created() {
    this.loadData();
  },
  watch: {
    // "View outbound" navigates between two Delivery-update routes, which Vue
    // would otherwise reuse without re-running created(). Reload on id change.
    "$route.params.id"() {
      this.loadData();
    },
  },
  methods: {
    ...deliveryMethods,
    ...salesDocMethods,
    async loadData() {
      this.returnedItems = [];
      this.returnLines = [];
      this.returnNotes = "";
      const r = await this.getDelivery({ id: this.$route.params.id });
      const d = r?.data || {};
      this.form = {
        status: d.status || "pending",
        recipient_name: d.recipient_name || "",
        notes: d.notes || "",
      };
      this.selectedStatus = this.form.status;

      // On a return, pull the DR document's lines from AutoCount so the returned
      // items + quantities are visible. deliveryreturn is allowed by the read
      // gate (it's a delivery doc). NOTE: getDelivery returns raw snake_case.
      if (d.type === "return" && d.autocount_doc_no) {
        try {
          const dr = await fetchSourceDoc("deliveryreturn", d.autocount_doc_no);
          const lines = dr?.data?.Details ?? dr?.data?.details ?? [];
          this.returnedItems = lines.map((line) => ({
            itemCode: line.ItemCode ?? line.item_code,
            description: line.Description ?? line.description,
            qty: Number(line.Qty ?? line.qty) || 0,
            uom: line.UOM ?? line.uom,
            unitPrice: Number(line.UnitPrice ?? line.unit_price) || 0,
          }));
        } catch (e) { /* DR not retrievable — leave items empty */ }
      }
    },
    onStatusChange(s) {
      this.selectedStatus = s;
      this.form.status = s || "";
    },
    async patchStatus() {
      this.saving = true;
      try {
        await this.updateDeliveryStatus({
          id: this.$route.params.id,
          status: this.form.status,
          extra: { recipient_name: this.form.recipient_name, notes: this.form.notes },
          filter: {},
        });
      } finally {
        this.saving = false;
      }
    },
    async loadReturnLines() {
      // Pull source DO lines from AutoCount to seed the return picker.
      if (this.returnLines.length || !this.d.autocountDocNo) return;
      let r;
      try {
        // Call the service directly so a missing source DO shows a clean
        // message instead of the raw AutoCount "Document not found" error.
        r = await fetchSourceDoc("deliveryorder", this.d.autocountDocNo);
      } catch (e) {
        this.$swal({
          icon: "warning",
          title: "Source delivery order unavailable",
          text: `The delivery order ${this.d.autocountDocNo} no longer exists in AutoCount, so its lines can't be loaded for a return.`,
        });
        return;
      }
      const details = r?.data?.Details ?? r?.data?.details ?? [];
      if (!details.length) {
        this.$swal({
          icon: "info",
          title: "No returnable lines",
          text: `No line items were found on ${this.d.autocountDocNo}.`,
        });
        return;
      }
      this.returnLines = details.map((line) => ({
        selected: false,
        itemCode: line.ItemCode ?? line.item_code,
        description: line.Description ?? line.description,
        uom: line.UOM ?? line.uom,
        unitPrice: Number(line.UnitPrice ?? line.unit_price) || 0,
        soldQty: Number(line.Qty ?? line.qty) || 0,
        taxCode: line.TaxCode ?? line.tax_code ?? "",
        returnQty: 0,
      }));
    },
    async submitReturn() {
      if (!this.canSubmitReturn) return;
      const items = this.selectedReturnLines.map((l) => ({
        ItemCode: l.itemCode,
        Description: l.description,
        Qty: Number(l.returnQty),
        UOM: l.uom,
        UnitPrice: l.unitPrice,
        TaxCode: l.taxCode,
      }));
      this.submittingReturn = true;
      try {
        const r = await this.createDeliveryReturn({
          id: this.$route.params.id,
          data: { items, notes: this.returnNotes },
          filter: {},
        });
        if (r) this.$router.push({ name: "Delivery-listing" });
      } finally {
        this.submittingReturn = false;
      }
    },
    cancel() { this.$router.push({ name: "Delivery-listing" }); },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12 col-lg-7">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center mb-3" style="gap: 10px;">
              <h5 class="card-title mb-0">{{ title }}</h5>
              <b-badge v-if="isOutbound" variant="primary">Outbound</b-badge>
              <b-badge v-else-if="isReturn" variant="warning">Return</b-badge>
            </div>

            <dl class="row">
              <dt class="col-sm-4">{{ isReturn ? 'DR Doc No' : 'DO Doc No' }}</dt>
              <dd class="col-sm-8">
                <b-badge variant="light" class="font-monospace">{{ d.autocountDocNo || '-' }}</b-badge>
              </dd>
              <dt class="col-sm-4">Source Doc</dt>
              <dd class="col-sm-8">{{ d.salesDocNo || '-' }}</dd>
              <template v-if="isReturn && d.parentUuid">
                <dt class="col-sm-4">Parent Delivery</dt>
                <dd class="col-sm-8">
                  <router-link :to="{ name: 'Delivery-update', params: { id: d.parentUuid } }" class="text-info">
                    <i class="mdi mdi-arrow-left mr-1"></i>View outbound
                  </router-link>
                </dd>
              </template>
              <dt class="col-sm-4">{{ $t('label.debtorCode') }}</dt><dd class="col-sm-8">{{ d.debtorCode || '-' }}</dd>
              <dt class="col-sm-4">Address</dt><dd class="col-sm-8">{{ d.deliveryAddress || '-' }}</dd>
              <dt class="col-sm-4">Driver</dt><dd class="col-sm-8">{{ d.driverName || '-' }}</dd>
              <template v-if="isReturn">
                <dt class="col-sm-4">Return Notes</dt><dd class="col-sm-8">{{ d.notes || '-' }}</dd>
              </template>
            </dl>

            <!-- Returned items (from the AutoCount DR document) -->
            <template v-if="isReturn">
              <h6 class="mt-4 text-primary">Returned Items</h6>
              <b-table-simple small responsive v-if="returnedItems.length">
                <b-thead>
                  <b-tr>
                    <b-th>Item</b-th>
                    <b-th>Description</b-th>
                    <b-th class="text-right">Qty</b-th>
                    <b-th>UoM</b-th>
                    <b-th class="text-right">Unit Price</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="(it, i) in returnedItems" :key="i">
                    <b-td class="font-monospace">{{ it.itemCode }}</b-td>
                    <b-td>{{ it.description }}</b-td>
                    <b-td class="text-right">{{ it.qty }}</b-td>
                    <b-td>{{ it.uom }}</b-td>
                    <b-td class="text-right">{{ it.unitPrice.toFixed(2) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p v-else class="text-muted small">No line details available for this return.</p>
            </template>

            <h6 class="mt-4 text-primary">Update Status</h6>
            <b-form @submit.prevent="patchStatus">
              <b-form-group :label="$t('label.status')">
                <multiselect
                  :value="selectedStatus"
                  :options="statuses"
                  :show-labels="false"
                  :allow-empty="false"
                  :placeholder="$t('label.select')"
                  @input="onStatusChange"
                />
              </b-form-group>
              <b-form-group label="Recipient Name">
                <b-form-input v-model="form.recipient_name" />
              </b-form-group>
              <b-form-group label="Notes">
                <b-form-textarea v-model="form.notes" rows="2" />
              </b-form-group>
              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button v-if="$can('Update Delivery')" type="submit" variant="primary" :disabled="saving">{{ $t('label.submit') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>

      <!-- Process Return panel — only on outbound deliveries -->
      <div class="col-12 col-lg-5" v-if="canProcessReturn">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="card-title mb-0">
                <i class="mdi mdi-undo-variant text-warning mr-1"></i>Process Return
              </h6>
              <b-button v-if="!returnLines.length" size="sm" variant="outline-warning" @click="loadReturnLines">
                <i class="mdi mdi-download mr-1"></i>Load DO lines
              </b-button>
            </div>
            <p class="text-muted small mb-3">
              Select items returned by the customer and enter return qty. An AutoCount Delivery Return doc will be created and stock added back.
            </p>

            <div v-if="returnLines.length">
              <div v-for="line in returnLines" :key="line.itemCode" class="return-line">
                <b-form-checkbox v-model="line.selected" class="line-check">
                  <span class="line-code">{{ line.itemCode }}</span>
                  <small class="text-muted d-block">{{ line.description }}</small>
                </b-form-checkbox>
                <div class="line-qty">
                  <small class="text-muted d-block">Sold: {{ line.soldQty }} {{ line.uom }}</small>
                  <b-form-input
                    type="number"
                    size="sm"
                    :max="line.soldQty"
                    min="0"
                    step="any"
                    :disabled="!line.selected"
                    v-model.number="line.returnQty"
                    :class="{ 'is-invalid': line.selected && line.returnQty > line.soldQty }"
                  />
                  <div v-if="line.selected && line.returnQty > line.soldQty" class="invalid-feedback d-block">
                    Return qty cannot exceed delivered qty ({{ line.soldQty }}).
                  </div>
                </div>
              </div>
              <b-form-group label="Return Notes" class="mt-3">
                <b-form-textarea v-model="returnNotes" rows="2" placeholder="Reason for return..." />
              </b-form-group>
              <div class="text-right">
                <b-button
                  v-if="$can('Create Delivery')"
                  variant="warning"
                  :disabled="!canSubmitReturn || submittingReturn"
                  @click="submitReturn"
                >
                  <b-spinner v-if="submittingReturn" small class="mr-1"></b-spinner>
                  Create Return ({{ selectedReturnLines.length }})
                </b-button>
              </div>
            </div>
            <div v-else class="text-muted text-center py-3">
              Click "Load DO lines" to fetch returnable items.
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.return-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
}
.return-line:last-child { border-bottom: 0; }
.line-check { flex: 1; }
.line-code { font-family: monospace; font-weight: 600; font-size: 13px; }
.line-qty { width: 110px; }
</style>
